import { Add, DeleteOutline, Edit } from '@mui/icons-material';
import { Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import {
    useMutateCreateCropMonitoringNote,
    useMutateDeleteCropMonitoringNote,
    useMutateUpdateCropMonitoringNote,
} from '/@/hooks/crop-monitoring-notes';
import { _ } from '/@/locale';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { EditActions } from './edit-actions';

const RegistryNote = ({ note }: { note: CropMonitoringNote }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [content, setContent] = useState(note.content ?? '');
    const updateNote = useMutateUpdateCropMonitoringNote();
    const deleteNote = useMutateDeleteCropMonitoringNote();

    const authorName = [note.createdBy?.user?.firstName, note.createdBy?.user?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();

    const handleSave = async () => {
        if (!content.trim()) return;
        await updateNote.trigger({ id: note.id, content: content.trim() });
        setIsEditing(false);
    };

    return (
        <Stack spacing={1} sx={{ bgcolor: 'grey.100', p: 1, overflow: 'auto', width: '100%' }}>
            {isEditing ? (
                <>
                    <TextField
                        fullWidth
                        multiline
                        value={content}
                        onChange={(event) => setContent(event.target.value)}
                        size='small'
                    />
                    <EditActions
                        onCancel={() => {
                            setContent(note.content ?? '');
                            setIsEditing(false);
                        }}
                        onSave={handleSave}
                        saveDisabled={!content.trim() || updateNote.isMutating}
                        cancelVariant='text'
                        size='small'
                    />
                </>
            ) : (
                <Stack direction='row' alignItems='flex-start' justifyContent='space-between' spacing={1}>
                    <Stack>
                        <Typography variant='caption' fontWeight='bold' color='primary'>
                            {`${authorName || _('Registrado')} - ${formatDate(new Date(note.created))}`}
                        </Typography>
                        <Typography variant='caption' color='primary'>
                            {note.content}
                        </Typography>
                    </Stack>
                    <Stack direction='row' alignItems='center'>
                        <IconButton size='small' aria-label={_('Editar')} onClick={() => setIsEditing(true)}>
                            <Edit fontSize='small' />
                        </IconButton>
                        <IconButton
                            size='small'
                            aria-label={_('Eliminar')}
                            disabled={deleteNote.isMutating}
                            onClick={() => deleteNote.trigger({ id: note.id })}
                        >
                            <DeleteOutline fontSize='small' />
                        </IconButton>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

const RegistryNotes = ({
    notes,
    fieldId,
    applicationRegisterId,
    applicationReportResultId,
    plantingDateCropMonitoringId,
    closedCropMonitoringId,
}: {
    notes: CropMonitoringNote[];
    fieldId: number;
    /** Attach new notes to a manual application register… */
    applicationRegisterId?: number;
    /** …or to a FieldView application report result… */
    applicationReportResultId?: number;
    /** …or to a planting (crop monitoring) record… */
    plantingDateCropMonitoringId?: number;
    /** …or to a closed (harvested) crop monitoring. Exactly one is provided. */
    closedCropMonitoringId?: number;
}) => {
    const [isAdding, setIsAdding] = useState(false);
    const [draft, setDraft] = useState('');
    const createNote = useMutateCreateCropMonitoringNote();

    const handleAdd = async () => {
        if (!draft.trim()) return;
        await createNote.trigger({
            field: fieldId,
            applicationRegister: applicationRegisterId,
            applicationReportResult: applicationReportResultId,
            plantingDateCropMonitoring: plantingDateCropMonitoringId,
            closedCropMonitoring: closedCropMonitoringId,
            content: draft.trim(),
        });
        setDraft('');
        setIsAdding(false);
    };

    return (
        <Stack spacing={1}>
            {notes.map((note) => (
                <RegistryNote key={note.id} note={note} />
            ))}

            {isAdding ? (
                <Stack spacing={1}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder={_('Escribir una observación')}
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                    />
                    <EditActions
                        onCancel={() => {
                            setDraft('');
                            setIsAdding(false);
                        }}
                        onSave={handleAdd}
                        saveDisabled={!draft.trim() || createNote.isMutating}
                        cancelVariant='text'
                        size='small'
                    />
                </Stack>
            ) : (
                <Button
                    variant='text'
                    color='primary'
                    size='small'
                    startIcon={<Add />}
                    sx={{ alignSelf: 'flex-start' }}
                    onClick={() => setIsAdding(true)}
                >
                    {_('Agregar una nota')}
                </Button>
            )}
        </Stack>
    );
};

export default RegistryNotes;
